import {
  ARCHETYPE_HOME_POSITIONS,
  type ArchetypeDefinition,
  type ArchetypeDetail,
  type ArchetypeStatus,
  type ArchetypeVersion,
  type MetricDataClass,
  type MetricDefinition,
} from "@/lib/admin-api";

// The editor's form model, kept free of React so the rules are tested on
// their own. The form is an ArchetypeDefinition with weights held as strings
// while they are typed.

export const POSITION_LABELS: Record<string, string> = {
  centre_back: "Centre-back",
  full_back: "Full-back",
  defensive_midfielder: "Defensive midfielder",
  central_midfielder: "Central midfielder",
  attacking_midfielder: "Attacking midfielder",
  winger: "Winger",
  striker: "Striker",
};

export function positionLabel(position: string): string {
  return POSITION_LABELS[position] ?? position;
}

/** Home positions in display order, as one line: "Winger, Striker". */
export function positionsLine(positions: readonly string[]): string {
  const order = (p: string) => {
    const index = (ARCHETYPE_HOME_POSITIONS as readonly string[]).indexOf(p);
    return index < 0 ? ARCHETYPE_HOME_POSITIONS.length : index;
  };
  return [...positions]
    .sort((a, b) => order(a) - order(b))
    .map(positionLabel)
    .join(", ");
}

export function statusLabel(status: ArchetypeStatus): string {
  switch (status) {
    case "draft":
      return "Draft";
    case "published":
      return "Published";
    case "retired":
      return "Retired";
    default:
      return "";
  }
}

export function formatArchetypeDate(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** Weights run from 0.5 to 10 in steps of 0.5 (profile-spec.md §5). */
export const WEIGHT_MIN = 0.5;
export const WEIGHT_MAX = 10;
export const WEIGHT_STEP = 0.5;
export const DEFAULT_WEIGHT = 1;

/** game-service's caps (archetype_admin.go maxArchetypeNameLen, maxArchetypeNotesLen). */
export const NAME_MAX = 60;
export const NOTES_MAX = 4000;

export interface WeightRow {
  metricKey: string;
  /** As typed. Parsed and checked by validateForm. */
  weight: string;
  /** The stored label and data class, used when the registry is unavailable. */
  storedLabel: string;
  storedDataClass: MetricDataClass;
}

export interface ArchetypeFormState {
  name: string;
  homePositions: string[];
  positional: boolean;
  eventOnly: boolean;
  weights: WeightRow[];
  notes: string;
}

export const EMPTY_FORM: ArchetypeFormState = {
  name: "",
  homePositions: [],
  positional: false,
  eventOnly: false,
  weights: [],
  notes: "",
};

export function formFromVersion(version: ArchetypeVersion): ArchetypeFormState {
  return {
    name: version.name,
    homePositions: [...version.homePositions],
    positional: version.positional,
    eventOnly: version.requiredData === "event",
    weights: version.weights.map((w) => ({
      metricKey: w.metricKey,
      weight: String(w.weight),
      storedLabel: w.label,
      storedDataClass: w.requiredData,
    })),
    notes: version.notes ?? "",
  };
}

/**
 * The version the form edits: the draft when there is one, else the
 * published version, else the newest version (a retired archetype). Saving
 * any of them writes the draft.
 */
export function editableVersion(detail: ArchetypeDetail): ArchetypeVersion | null {
  return detail.draft ?? detail.published ?? detail.versions[0] ?? null;
}

/** What the editor tracks besides the form: the revision a save must echo. */
export interface EditorSeed {
  form: ArchetypeFormState;
  /** The draft's revision as last read, 0 when there is no draft. */
  expectedRevision: number;
  /** The draft's version, 0 when there is no draft. */
  draftVersion: number;
}

export function seedFromDetail(detail: ArchetypeDetail): EditorSeed {
  const version = editableVersion(detail);
  return {
    form: version ? formFromVersion(version) : EMPTY_FORM,
    expectedRevision: detail.draft?.revision ?? 0,
    draftVersion: detail.draft?.version ?? 0,
  };
}

export function parseWeight(raw: string): number | null {
  const value = Number(raw.trim());
  if (raw.trim() === "" || !Number.isFinite(value)) return null;
  return value;
}

export function validWeight(value: number | null): boolean {
  if (value == null) return false;
  if (value < WEIGHT_MIN || value > WEIGHT_MAX) return false;
  return Number.isInteger(value / WEIGHT_STEP);
}

/** The metric's data class: the registry's when it knows the key, else the stored one. */
export function weightDataClass(
  row: WeightRow,
  metrics: ReadonlyMap<string, MetricDefinition>,
): MetricDataClass {
  return metrics.get(row.metricKey)?.dataClass ?? row.storedDataClass;
}

export function weightLabel(row: WeightRow, metrics: ReadonlyMap<string, MetricDefinition>) {
  return metrics.get(row.metricKey)?.label || row.storedLabel || row.metricKey;
}

/**
 * Why the form cannot be saved, one line each, or [] when it can. The same
 * rules game-service applies, checked here so the common mistakes do not
 * take a round trip. game-service stays the judge of registry keys.
 */
export function validateForm(
  form: ArchetypeFormState,
  metrics: ReadonlyMap<string, MetricDefinition>,
): string[] {
  const problems: string[] = [];
  const name = form.name.trim();
  if (!name) problems.push("Name is required.");
  else if ([...name].length > NAME_MAX) problems.push(`Names are at most ${NAME_MAX} characters.`);
  if (form.homePositions.length === 0) problems.push("A home position is required.");
  if (form.weights.length === 0) {
    problems.push("A metric weight is required.");
  } else {
    if (form.weights.some((row) => !validWeight(parseWeight(row.weight)))) {
      problems.push(`Weights run from ${WEIGHT_MIN} to ${WEIGHT_MAX} in steps of ${WEIGHT_STEP}.`);
    }
    const classes = form.weights.map((row) => weightDataClass(row, metrics));
    if (!form.eventOnly && classes.every((c) => c === "event")) {
      problems.push("Without event data only, a season stats metric is required.");
    }
    if (form.eventOnly && !classes.includes("event")) {
      problems.push("Event data only requires an event data metric.");
    }
  }
  return problems;
}

export function definitionFromForm(form: ArchetypeFormState): ArchetypeDefinition {
  return {
    name: form.name.trim(),
    homePositions: (ARCHETYPE_HOME_POSITIONS as readonly string[]).filter((p) =>
      form.homePositions.includes(p),
    ),
    weights: form.weights.map((row) => ({
      metricKey: row.metricKey,
      weight: parseWeight(row.weight) ?? 0,
    })),
    requiredData: form.eventOnly ? "event" : "season_stats",
    positional: form.positional,
    notes: form.notes.trim(),
  };
}

/** True when the two forms would save the same definition. */
export function sameDefinition(a: ArchetypeFormState, b: ArchetypeFormState): boolean {
  return JSON.stringify(definitionFromForm(a)) === JSON.stringify(definitionFromForm(b));
}

/** "Box Threat" to "box-threat": the id shape admin-service accepts. */
export function slugFromName(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64)
    .replace(/-+$/g, "");
}

const ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function validArchetypeId(id: string): boolean {
  return id.length > 0 && id.length <= 64 && ID_PATTERN.test(id);
}

/**
 * The metrics the picker offers: every registry metric not already weighted,
 * in registry order.
 */
export function pickableMetrics(
  all: readonly MetricDefinition[],
  weights: readonly WeightRow[],
): MetricDefinition[] {
  const used = new Set(weights.map((w) => w.metricKey));
  return [...all].filter((m) => !used.has(m.key)).sort((a, b) => a.order - b.order);
}
